import { supabase } from '@/lib/supabase/client';
import type {
  InvoiceEquipmentResolution,
  InvoiceLocationProvisioningInput,
  InvoiceLocationProvisioningResult,
  InvoicePaidEquipmentPlan,
  InvoicePaidEquipmentResult,
  InvoicePaidWorkItem,
  InvoicePaidWorkItemEvent,
} from '@/types/invoice-paid-work';
import type { LocationOrder } from '@/types/location-orders';

function throwReadError(operation: string, message: string): never {
  throw new Error(`Failed to ${operation}: ${message}`);
}

async function assertPaidWorkAdmin(): Promise<void> {
  const { data, error } = await supabase.rpc('is_tpb_admin');

  if (error) {
    throwReadError('verify paid invoice work access', error.message);
  }

  if (data !== true) {
    throwReadError('verify paid invoice work access', 'this account is not an authorized TPB admin');
  }
}

export const invoicePaidWorkService = {
  async getAll(): Promise<InvoicePaidWorkItem[]> {
    await assertPaidWorkAdmin();
    const { data, error } = await supabase
      .from('invoice_paid_work_items')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      throwReadError('fetch paid invoice work', error.message);
    }

    return (data ?? []) as InvoicePaidWorkItem[];
  },

  async getByInvoiceId(invoiceId: string): Promise<InvoicePaidWorkItem | null> {
    await assertPaidWorkAdmin();
    const { data, error } = await supabase
      .from('invoice_paid_work_items')
      .select('*')
      .eq('invoice_id', invoiceId)
      .maybeSingle();

    if (error) {
      throwReadError('fetch paid invoice work item', error.message);
    }

    return (data as InvoicePaidWorkItem | null) ?? null;
  },

  async getByInvoiceIds(invoiceIds: string[]): Promise<InvoicePaidWorkItem[]> {
    if (invoiceIds.length === 0) return [];

    await assertPaidWorkAdmin();
    const { data, error } = await supabase
      .from('invoice_paid_work_items')
      .select('*')
      .in('invoice_id', Array.from(new Set(invoiceIds)));

    if (error) {
      throwReadError('fetch paid invoice work items', error.message);
    }

    return (data ?? []) as InvoicePaidWorkItem[];
  },

  async getEvents(workItemId: string): Promise<InvoicePaidWorkItemEvent[]> {
    await assertPaidWorkAdmin();
    const { data, error } = await supabase
      .from('invoice_paid_work_item_events')
      .select('*')
      .eq('work_item_id', workItemId)
      .order('created_at', { ascending: true });

    if (error) {
      throwReadError('fetch paid invoice work events', error.message);
    }

    return (data ?? []) as InvoicePaidWorkItemEvent[];
  },

  async getLocationOrdersForReview(locationId: string): Promise<LocationOrder[]> {
    await assertPaidWorkAdmin();
    const { data, error } = await supabase
      .from('location_orders')
      .select('*')
      .eq('location_id', locationId)
      .order('order_date', { ascending: false });

    if (error) {
      throwReadError('fetch location orders for paid-work review', error.message);
    }

    return (data ?? []) as LocationOrder[];
  },

  async completeNewLocation(
    input: InvoiceLocationProvisioningInput
  ): Promise<InvoiceLocationProvisioningResult> {
    const { data, error } = await supabase
      .rpc('complete_invoice_location_provisioning', {
        p_work_item_id: input.workItemId,
        p_location_number: input.locationNumber,
        p_location_name: input.locationName,
        p_location_status: input.locationStatus,
        p_address: input.address ?? null,
        p_contract_signed_date: input.contractSignedDate ?? null,
        p_contract_renewal_date: input.contractRenewalDate ?? null,
        p_contract_term_months: input.contractTermMonths ?? null,
        p_contract_signee_name: input.contractSigneeName ?? null,
        p_contract_signee_title: input.contractSigneeTitle ?? null,
        p_installation_date: input.installationDate ?? null,
        p_launch_date: input.launchDate ?? null,
        p_monthly_license_fee: input.monthlyLicenseFee ?? null,
        p_license_fee_period: input.licenseFeePeriod ?? null,
        p_pos_system: input.posSystem ?? null,
        p_customer_account_id: input.customerAccountId ?? null,
        p_contract_anchor_location_id: input.contractAnchorLocationId ?? null,
      })
      .single();

    if (error) {
      throw new Error(`Failed to create location: ${error.message}`);
    }

    const result = data as InvoiceLocationProvisioningResult;
    if (!result.ok) {
      const message = result.error_message || 'Location conversion failed.';
      throw new Error(result.error_code ? `[${result.error_code}] ${message}` : message);
    }

    return result;
  },

  async prepareEquipment(workItemId: string): Promise<InvoicePaidEquipmentPlan> {
    const { data, error } = await supabase
      .rpc('prepare_invoice_paid_equipment', { p_work_item_id: workItemId })
      .single();

    if (error) {
      throw new Error(`Failed to prepare equipment sync: ${error.message}`);
    }

    const result = data as InvoicePaidEquipmentPlan;
    if (!result.ok) {
      throw new Error(result.error_message || 'Equipment sync is not ready.');
    }

    return result;
  },

  async applyEquipment(
    workItemId: string,
    plan: InvoicePaidEquipmentPlan,
    resolutions: Map<string, InvoiceEquipmentResolution>
  ): Promise<InvoicePaidEquipmentResult> {
    const displayTypeIds = Array.from(resolutions.keys());
    const resolutionValues = displayTypeIds.map((displayTypeId) => resolutions.get(displayTypeId)!);
    const reviewedItems = new Map(
      plan.items.filter((item) => item.conflict).map((item) => [item.display_type_id, item])
    );
    const expectedCurrentQuantities = displayTypeIds.map(
      (displayTypeId) => reviewedItems.get(displayTypeId)?.current_quantity ?? -1
    );
    const expectedInvoiceQuantities = displayTypeIds.map(
      (displayTypeId) => reviewedItems.get(displayTypeId)?.invoice_quantity ?? -1
    );
    const { data, error } = await supabase
      .rpc('apply_invoice_paid_equipment', {
        p_work_item_id: workItemId,
        p_display_type_ids: displayTypeIds,
        p_resolutions: resolutionValues,
        p_expected_current_quantities: expectedCurrentQuantities,
        p_expected_invoice_quantities: expectedInvoiceQuantities,
      })
      .single();

    if (error) {
      throw new Error(`Failed to apply equipment: ${error.message}`);
    }

    const result = data as InvoicePaidEquipmentResult;
    if (!result.ok) {
      throw new Error(result.error_message || 'Equipment sync failed.');
    }

    return result;
  },
};
